import {AxiosError} from 'axios';
import {InfiniteData, useInfiniteQuery, UseInfiniteQueryOptions, useQueryClient} from '@tanstack/react-query';

import {api} from '@eksab/api';

import type {Article} from '../types';
import {useAccessToken} from '@eksab/hooks';

type ArticlesQueryParams =
  | {
      query?: string;
    }
  | undefined;

const queryKey = ({query}: ArticlesQueryParams = {}, isLoggedIn: boolean) => ['news', 'infinite', query, isLoggedIn];

const queryFn = ({query}: ArticlesQueryParams = {}, page = 1, isLoggedIn: boolean) =>
  api
    .get<WithPagination<Article[]>>(`v3/news${isLoggedIn ? '/auth' : ''}?per_page=10`, {params: {q: query, page}})
    .then((res) => res.data);

export const useInfiniteArticlesQuery = (
  {query}: ArticlesQueryParams = {},
  options?: UseInfiniteQueryOptions<WithPagination<Article[]>, AxiosError>,
) => {
  const isLoggedIn = !!useAccessToken()?.data;
  const queryClient = useQueryClient();
  const placeholderData = queryClient.getQueryData<InfiniteData<WithPagination<Article[]>>>([
    ...queryKey(undefined, false),
  ]);

  return useInfiniteQuery<WithPagination<Article[]>, AxiosError>(
    queryKey({query}, isLoggedIn),
    ({pageParam}) => queryFn({query}, pageParam, isLoggedIn),
    {
      ...options,
      getNextPageParam: ({meta: {current_page = 1, last_page = 100} = {}}, pages) =>
        pages.length < last_page ? current_page + 1 : undefined,
      placeholderData,
    },
  );
};

useInfiniteArticlesQuery.queryFn = queryFn;
useInfiniteArticlesQuery.queryKey = queryKey;
